"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import PhosphorIcon from "@/shared/components/common/PhosphorIcon";
import { getPhosphorFacilityIconName } from "@/shared/libs/hotel-facility-icons";
import type { HotelDetailV2Facility } from "../model/hotelDetailV2.model";
import { EmptyContent } from "./AboutSection";

interface AmenitiesSectionProps {
  facilities: HotelDetailV2Facility[];
  hotelName: string;
}

export default function AmenitiesSection({
  facilities,
  hotelName,
}: AmenitiesSectionProps) {
  const [open, setOpen] = useState(false);
  const visibleFacilities = facilities.slice(0, 12);

  return (
    <section
      id="amenities"
      className="scroll-mt-28 rounded-lg border border-[rgb(229,231,235)] bg-white p-5"
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-base font-bold text-[#1a1a2e]">
          <PhosphorIcon name="Sparkle" size={18} className="text-primary" />
          امکانات {hotelName}
        </h2>
        {facilities.length > 0 ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs font-bold text-primary transition hover:bg-primary/10"
          >
            <PhosphorIcon name="Stack" size={15} className="shrink-0" />
            <span>مشاهده همه امکانات</span>
          </button>
        ) : null}
      </div>

      {facilities.length > 0 ? (
        <div className="flex flex-wrap items-start gap-2.5">
          {visibleFacilities.map((facility) => (
            <FacilityItem key={facility.id} facility={facility} />
          ))}
        </div>
      ) : (
        <EmptyContent text="امکانات این هتل هنوز ثبت نشده است." />
      )}

      {open ? (
        <AmenitiesDialog
          facilities={facilities}
          hotelName={hotelName}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </section>
  );
}

function FacilityItem({ facility }: { facility: HotelDetailV2Facility }) {
  const icon = getPhosphorFacilityIconName(facility.icon);
  return (
    <div className="inline-flex w-fit max-w-full items-center gap-2 rounded-lg border border-[rgb(229,231,235)] px-3 py-2 text-right transition hover:border-emerald-200">
      {icon ? (
        <PhosphorIcon
          name={icon}
          size={17}
          className="shrink-0 text-emerald-600"
        />
      ) : null}
      <span className="min-w-0 text-xs font-medium leading-5 text-[#1a1a2e]">{facility.title}</span>
      {facility.description ? (
        <PhosphorIcon name="Info" size={11} className="shrink-0 text-[#9ca3af]" />
      ) : null}
    </div>
  );
}

function AmenitiesDialog({
  facilities,
  hotelName,
  onClose,
}: AmenitiesSectionProps & { onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const groups = useMemo(() => groupFacilities(facilities), [facilities]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "Tab") {
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable?.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus();
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[250] grid place-items-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="amenities-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <header className="flex items-center justify-between border-b border-[#e5e7eb] px-5 py-4">
          <div>
              <h2 id="amenities-title" className="text-base font-bold text-[#1a1a2e]">
              امکانات و ویژگی‌ها
            </h2>
            <p className="mt-0.5 text-xs text-[#6b7280]">{hotelName}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="بستن امکانات"
            className="grid h-9 w-9 place-items-center rounded-full hover:bg-slate-100"
          >
            <PhosphorIcon name="X" size={18} />
          </button>
        </header>
        <div className="space-y-6 overflow-y-auto p-5">
          {groups.map(([group, items], index) => (
            <section key={group ?? `ungrouped-${index}`}>
              {group ? (
                <h3 className="mb-3 text-sm font-bold text-[#1a1a2e]">{group}</h3>
              ) : null}
              <div className="flex flex-wrap items-start gap-2.5">
                {items.map((facility) => (
                  <div key={facility.id} className="w-fit max-w-full">
                    <FacilityItem facility={facility} />
                    {facility.description ? (
                      <p className="mr-6 mt-1 text-xs leading-5 text-[#9ca3af]">
                        {facility.description}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

function groupFacilities(facilities: HotelDetailV2Facility[]) {
  const groups = new Map<string | undefined, HotelDetailV2Facility[]>();
  facilities.forEach((facility) => {
    const group = facility.group;
    groups.set(group, [...(groups.get(group) || []), facility]);
  });
  return [...groups.entries()];
}
